import { useEffect, useState } from 'react'
import type { MeDto, RedeemInfoDto } from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { Brandmark } from '../components/Brandmark.js'
import { useRichText, useText } from '../i18n.js'
import { useNavigate } from '../router.js'

/**
 * Einmal-Link einlösen.
 *
 * Dieselbe Maske für beide Fälle — vergessenes Passwort und Einladung in eine
 * Organisation. Der Unterschied ist die Wortwahl und die Frage nach dem Namen:
 * Wer eingeladen wurde, hat seinen noch nie selbst geschrieben, sondern die
 * Verwaltung hat ihn geraten.
 */
export function RedeemPage({
  token,
  onSignedIn,
}: {
  token: string
  onSignedIn: (user: MeDto) => void
}): React.ReactElement {
  const t = useText()
  const rich = useRichText()
  const [info, setInfo] = useState<RedeemInfoDto | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    api
      .redeemInfo(token)
      .then((loaded) => {
        setInfo(loaded)
        setDisplayName(loaded.displayName)
      })
      .catch((caught: unknown) =>
        setFailed(caught instanceof ApiError ? caught.message : t('redeem.checkFailed')),
      )
  }, [token, t])

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    if (password !== repeat) {
      setError(t('redeem.mismatch'))
      return
    }
    setError(null)
    setBusy(true)
    try {
      const user = await api.redeem(token, password, displayName.trim() || undefined)
      // Erst die Adresse aufräumen, dann anmelden: Sonst bliebe der verbrauchte
      // Token im Verlauf des Browsers stehen und im nächsten Aufruf im Nichts.
      navigate('/', true)
      onSignedIn(user)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('common.failed'))
    } finally {
      setBusy(false)
    }
  }

  if (failed) {
    return (
      <div className="auth-screen brand-surface">
        <div className="auth-card">
          <Brandmark />
          <p className="auth-lead">{failed}</p>
          <p className="muted small">{t('redeem.expiredHint')}</p>
          <button className="button primary" onClick={() => navigate('/', true)}>
            {t('redeem.toSignIn')}
          </button>
        </div>
      </div>
    )
  }

  if (!info) {
    return (
      <div className="auth-screen brand-surface">
        <p className="muted">{t('common.moment')}</p>
      </div>
    )
  }

  const isInvite = info.purpose === 'invite'

  return (
    <div className="auth-screen brand-surface">
      <form className="auth-card" onSubmit={submit}>
        <Brandmark />
        <p className="auth-lead">
          {isInvite
            ? rich('redeem.invited', { org: <strong>{info.orgName}</strong> })
            : t('redeem.reset', { email: info.email })}
        </p>

        {isInvite && (
          <label className="field">
            <span>{t('redeem.yourName')}</span>
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              autoComplete="name"
              placeholder={t('login.namePlaceholder')}
            />
            <small>{t('redeem.nameHint')}</small>
          </label>
        )}

        {/*
          Die Adresse steht fest — sie hängt am Link. Als ausgegrautes Feld
          statt als Fließtext, weil Passwortverwalter sie sonst nicht dem neuen
          Passwort zuordnen können.
        */}
        <label className="field">
          <span>{t('common.email')}</span>
          <input type="email" value={info.email} autoComplete="username" readOnly disabled />
        </label>

        <label className="field">
          <span>{t('account.newPassword')}</span>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
          />
          <small>{t('account.minLength')}</small>
        </label>

        <label className="field">
          <span>{t('account.repeat')}</span>
          <input
            type="password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
          />
        </label>

        {error && <p className="form-error">{error}</p>}

        <button className="button primary" type="submit" disabled={busy}>
          {busy ? t('common.moment') : isInvite ? t('redeem.start') : t('redeem.setPassword')}
        </button>
      </form>
    </div>
  )
}
